import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, MapPin, Star, Ticket, WalletCards } from 'lucide-react';
import { Button } from './ui/button';
import {
  getSupplierLocationLabel,
  getSupplierPlanMeta,
  SUPPLIER_COVER_FALLBACK,
} from '../lib/suppliers';

export default function SupplierCard({ supplier, compact = false }) {
  const planMeta = getSupplierPlanMeta(supplier?.plan_type);
  const profileHref = `/fornecedores/${encodeURIComponent(supplier?.slug || '')}`;

  return (
    <article className={`overflow-hidden rounded-[1.6rem] border ${planMeta.cardClass} ${planMeta.frameClass} transition duration-300 hover:-translate-y-1`}>
      <div className={`relative ${compact ? 'aspect-[4/3]' : 'aspect-[16/10]'} overflow-hidden`}>
        <img
          src={supplier?.cover_image || SUPPLIER_COVER_FALLBACK}
          alt={supplier?.public_name || supplier?.company_name || 'Fornecedor'}
          className="h-full w-full object-cover transition duration-500 hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#1C1C1C]/45 via-[#1C1C1C]/10 to-transparent" />
        {(supplier?.is_homepage_featured || supplier?.plan_type === 'premium' || supplier?.plan_type === 'premium_plus') ? (
          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            {supplier?.is_homepage_featured ? (
              <span className="inline-flex rounded-full bg-white/92 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#7A5D52]">
                Destaque
              </span>
            ) : null}
            {(supplier?.plan_type === 'premium' || supplier?.plan_type === 'premium_plus') ? (
              <span className="inline-flex rounded-full bg-[#1C1C1C]/82 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                {planMeta.highlightLabel}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="space-y-4 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${planMeta.badgeClass}`}>
            {planMeta.label}
          </span>
          {supplier?.is_verified ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
              <BadgeCheck className="h-3.5 w-3.5" />
              Verificado
            </span>
          ) : null}
        </div>

        <div>
          <h3 className="text-2xl text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
            {supplier?.public_name || supplier?.company_name}
          </h3>
          <p className="mt-1 text-sm font-medium text-[#9A6B65]">{supplier?.category || 'Fornecedor de casamento'}</p>
        </div>

        {(supplier?.rating || supplier?.review_count) ? (
          <div className="flex flex-wrap items-center gap-2 text-sm text-[#5A4C49]">
            <span className="inline-flex items-center gap-1 font-semibold text-[#1C1C1C]">
              <Star className="h-4 w-4 fill-[#D6A62F] text-[#D6A62F]" />
              {Number(supplier.rating || 0).toFixed(1)}
            </span>
            <span>({supplier.review_count || 0})</span>
            <span>·</span>
            <span>{supplier.city || supplier.state || 'Brasil'}</span>
          </div>
        ) : null}

        <p className="flex items-center gap-2 text-sm text-[#5A4C49]">
          <MapPin className="h-4 w-4 text-[#C8A97E]" />
          {getSupplierLocationLabel(supplier)}
        </p>

        {supplier?.short_description ? (
          <p className="min-h-[3.9rem] text-sm leading-relaxed text-[#333333]">
            {supplier.short_description}
          </p>
        ) : null}

        {(supplier?.starting_price || supplier?.promotion_count) ? (
          <div className="grid gap-2 text-sm text-[#4B403D]">
            {supplier?.starting_price ? (
              <p className="inline-flex items-center gap-2">
                <WalletCards className="h-4 w-4 text-[#9A6B65]" />
                A partir de R${Number(supplier.starting_price).toLocaleString('pt-BR')}
              </p>
            ) : null}
            {supplier?.promotion_count ? (
              <p className="inline-flex items-center gap-2">
                <Ticket className="h-4 w-4 text-[#9A6B65]" />
                {supplier.promotion_count} promocao
                {supplier.promotion_discount ? <span className="text-[#D94B63]">-{supplier.promotion_discount}%</span> : null}
              </p>
            ) : null}
          </div>
        ) : null}

        <Link to={profileHref}>
          <Button className={`${supplier?.plan_type === 'premium_plus' ? 'bg-[#1C1C1C] hover:bg-[#333333]' : 'bg-[#E8B7B1] hover:bg-[#DDA39D]'} h-11 rounded-full px-5 text-white`}>
            Ver perfil
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </article>
  );
}
